import { Icon } from '@scale/scaleui';
import { Attachment, ModelInputFileAttachmentType, ReferenceText } from '@scale/shared/types/chat';
import { useCallback, useState } from 'react';
import EditAttachmentsModal from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/EditAttachmentsModal';
import EditReferenceTextModal from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/EditReferenceTextModal';
import ReferenceTextChip from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/ReferenceTextChip';
import ReferenceTextWithCategoryChip from 'react-scale/components/scalers/tasks/ThreadedInteraction/attachments/ReferenceTextWithCategoryChip';
import apiFetch from 'react-scale/lib/api-fetch';

type ReferenceTextGroupProps = {
  referenceTexts: ReferenceText[];
  setReferenceTexts: (referenceTexts: ReferenceText[]) => void;
  maxReferenceTexts?: number;
  readOnly?: boolean;
  required?: boolean;
  hideUrl?: boolean;
  showReferenceCategory?: boolean;
  allowSelectPdfAttachmentAsReferenceText?: boolean;
  editing?: boolean;
  referenceTextCategories?: string;
  minReferenceTextsLength?: number;
  maxReferenceTextsLength?: number;
};

const ReferenceTextGroup = ({
  referenceTexts,
  required = false,
  editing = false,
  setReferenceTexts,
  readOnly,
  hideUrl = false,
  showReferenceCategory = false,
  maxReferenceTexts = 1,
  allowSelectPdfAttachmentAsReferenceText = false,
  referenceTextCategories,
  minReferenceTextsLength,
  maxReferenceTextsLength,
}: ReferenceTextGroupProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showAttachmentModal, setShowAttachmentModal] = useState<boolean>(false);
  const [isLoadingPdfText, setIsLoadingPdfText] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const extractPdfText = useCallback(
    async (
      attachmentUrl: string,
      attachmentMimeType: string,
    ): Promise<{ text: string; wordCount: number } | undefined> => {
      setIsLoadingPdfText(true);
      try {
        const { text, wordCount } = await apiFetch(null, '/internal/genai/extractPdfText', {
          method: 'POST',
          body: {
            url: attachmentUrl,
            mimeType: attachmentMimeType,
          },
        });
        setIsLoadingPdfText(false);
        return { text, wordCount };
      } catch (error) {
        setError(`Failed to extract PDF text: ${error}`);
        setIsLoadingPdfText(false);
      }
    },
    [],
  );

  // Avoid rendering a blank mt-2 div if there's nothing to render
  if (
    !(referenceTexts && referenceTexts.length > 0) &&
    !(!readOnly && referenceTexts.length < maxReferenceTexts)
  ) {
    return null;
  }

  return (
    <div className="flex gap-2 mt-2 flex-wrap">
      {referenceTexts && referenceTexts.length > 0 && (
        <>
          {referenceTexts.map((referenceText: ReferenceText, index: number) =>
            allowSelectPdfAttachmentAsReferenceText ? (
              <ReferenceTextWithCategoryChip
                key={index}
                index={index}
                referenceText={referenceText}
                setReferenceText={(referenceText: ReferenceText) => {
                  const newReferenceTexts = [...referenceTexts];
                  newReferenceTexts[index] = referenceText;
                  setReferenceTexts(newReferenceTexts);
                }}
                deleteReferenceText={() => {
                  const newReferenceTexts = [...referenceTexts];
                  newReferenceTexts.splice(index, 1);
                  setReferenceTexts(newReferenceTexts);
                }}
                showReferenceCategory={showReferenceCategory}
                readOnly={readOnly}
                referenceTextCategories={referenceTextCategories}
                minReferenceTextsLength={minReferenceTextsLength}
                maxReferenceTextsLength={maxReferenceTextsLength}
              />
            ) : (
              <ReferenceTextChip
                name={referenceText.name || `Reference Text ${index + 1}`}
                editing={editing}
                required={required}
                hideUrl={hideUrl || allowSelectPdfAttachmentAsReferenceText}
                showReferenceCategory={showReferenceCategory}
                key={index}
                referenceText={referenceText}
                setReferenceText={(referenceText: ReferenceText) => {
                  const newReferenceTexts = [...referenceTexts];
                  newReferenceTexts[index] = referenceText;
                  setReferenceTexts(newReferenceTexts);
                }}
                deleteReferenceText={() => {
                  const newReferenceTexts = [...referenceTexts];
                  newReferenceTexts.splice(index, 1);
                  setReferenceTexts(newReferenceTexts);
                }}
                readOnly={readOnly}
                minReferenceTextsLength={minReferenceTextsLength}
                maxReferenceTextsLength={maxReferenceTextsLength}
              />
            ),
          )}
        </>
      )}
      {!readOnly &&
        referenceTexts.length < maxReferenceTexts &&
        !allowSelectPdfAttachmentAsReferenceText && (
          <>
            <Icon
              hasBackground
              size="sm"
              icon="plus"
              variant="primary"
              onClick={() => {
                setShowModal(!showModal);
              }}
              tooltip="Add Reference Texts"
            />
            <EditReferenceTextModal
              isOpen={showModal}
              required={required}
              hideUrl={hideUrl || allowSelectPdfAttachmentAsReferenceText}
              showReferenceCategory={showReferenceCategory}
              onClose={() => setShowModal(false)}
              referenceText={{ content: '' }}
              setReferenceText={(referenceText: ReferenceText) => {
                setReferenceTexts([...referenceTexts, referenceText]);
              }}
              setIsOpen={setShowModal}
              minReferenceTextsLength={minReferenceTextsLength}
              maxReferenceTextsLength={maxReferenceTextsLength}
            />
          </>
        )}
      {!readOnly && allowSelectPdfAttachmentAsReferenceText && (
        <>
          <Icon
            hasBackground
            size="sm"
            icon="upload"
            variant="primary"
            onClick={() => {
              setShowAttachmentModal(!showAttachmentModal);
            }}
            tooltip="Upload PDF or text files as reference texts"
          />
          <EditAttachmentsModal
            title="Add PDF or Text Files"
            isOpen={showAttachmentModal}
            onClose={() => setShowAttachmentModal(false)}
            attachments={[]}
            multipleFiles={true}
            setAttachments={async (attachments: Attachment[]) => {
              setShowAttachmentModal(false);
              setIsLoadingPdfText(true);
              setError(undefined);
              try {
                const newReferenceTexts: ReferenceText[] = [];
                // TODO: Use pMap to do PDF extraction in parallel
                for (const attachment of attachments) {
                  if (attachment.url) {
                    const result = await extractPdfText(attachment.url, attachment.mimeType);
                    if (result) {
                      const { text, wordCount } = result;
                      newReferenceTexts.push({
                        content: text,
                        url: attachment.url,
                        name: attachment.name,
                        wordCount,
                      });
                    }
                  }
                }
                setReferenceTexts(referenceTexts.concat(newReferenceTexts));
              } catch (err) {
                setError(`Failed to process uploaded files: ${err}`);
              } finally {
                setIsLoadingPdfText(false);
              }
            }}
            supportedMimeTypesByInputType={{
              [ModelInputFileAttachmentType.Text]: ['text/*'],
              [ModelInputFileAttachmentType.Pdf]: ['application/pdf'],
              [ModelInputFileAttachmentType.Image]: [],
              [ModelInputFileAttachmentType.Json]: [],
              [ModelInputFileAttachmentType.Audio]: [],
              [ModelInputFileAttachmentType.Document]: ['application/pdf'],
              [ModelInputFileAttachmentType.Zip]: [],
            }}
            shouldMirrorImageUrlsToTempBucket={false}
            enableAttachments={true}
            enableImageUrls={false}
            enableMmImageService={false}
            fileSizeLimitInBytes={100000000}
            beforeFileProcessing={() => {
              setShowAttachmentModal(false);
            }}
          />
          {isLoadingPdfText && <div>Extracting reference text...</div>}
          {error && <div>{error}</div>}
        </>
      )}
      {maxReferenceTexts && (
        <div className="mt-2 text-xs text-neutral-500">
          References: {referenceTexts.length} / {maxReferenceTexts}
        </div>
      )}
      {maxReferenceTexts && referenceTexts.length > maxReferenceTexts && (
        <div className="mt-2 text-xs text-neutral-500">
          You have reached the maximum number of reference texts. Please remove some reference
          texts.
        </div>
      )}
      {allowSelectPdfAttachmentAsReferenceText && referenceTexts.length > 0 && (
        <div className="mt-2 text-xs text-neutral-500">
          Total reference words (approx.):{' '}
          {referenceTexts.reduce((acc, curr) => acc + (curr.wordCount || 0), 0)}
        </div>
      )}
    </div>
  );
};

export default ReferenceTextGroup;
